import { PlusIcon } from '@common_components/icons/plus.icon';
import { ArrowUpIcon } from '@common_components/icons/arrowUp.icon';
import { ArrowDownIcon } from '@common_components/icons/arrowDown.icon';
import { TrashIcon } from '@common_components/icons/trash.icon';
import SelectInput, { GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import InputText from '@common_components/inputs/textInput.component';
import { CheckIcon, RootState, UUID } from '@umanforge/common';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FieldSettingService } from 'services/fieldSetting.service';
import { GlobalService } from 'services/global.service';
import { FieldSettingPayload, Props, Setting } from 'interfaces/fieldSetting.interface';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';
import DataChecker from 'components/common/dataChecker';


const SettingsSite = ( { selectedSiteId, customSettings, clients, sites, setCustomSettings }: Props ) => {
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const { t } = useTranslation();

  const [ editedIndexes, setEditedIndexes ] = React.useState<Set<number>>( new Set() );
  const [ originalSettings, setOriginalSettings ] = React.useState<Setting[]>( [] );
  const [ isHalfScreen, setIsHalfScreen ] = useState<boolean>( false );
  const [ newSetting, setNewSetting ] = React.useState( {
    name: '',
    type: 'text' as 'text' | 'number' | 'date' | 'list',
    textMaxLength: String( MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE ),
    numberMin: '',
    numberMax: '',
    constraint: '',
  } );

  const isDuplicateName = originalSettings.some( setting => {
    return setting.name.trim().toLowerCase() === newSetting.name.trim().toLowerCase();
  } );

  useEffect( () => {
    if ( !isValidSiteId() ) {
      setCustomSettings( [] );
      return;
    }

    const fetchSettings = async () => {
      try {
        const settings = await FieldSettingService.getBySiteId( selectedSiteId!, token, dispatch );

        const adapted = ( Array.isArray( settings ) ? settings : [ settings ] )
          .filter( s => {
            const hasValidId = s.id && GlobalService.isValidGuid( s.id );
            return hasValidId;
          } )
          .map( s => {
            const type = s.type === 'string' ? 'text' : s.type === 'int' ? 'number' : s.type;
            let localConstraint = '';
            if ( type === 'text' ) {
              localConstraint = String( s.textMaxLength || '' );
            } else if ( type === 'number' ) {
              localConstraint = [ s.numberMin, s.numberMax ].filter( v => {return v !== undefined;} ).join( ',' );
            } else if ( type === 'list' ) {
              localConstraint = Array.isArray( s.listItems ) ? s.listItems.join( ',' ) : '';
            }
            const adaptedSetting = {
              name: s.name || '',
              type,
              constraint: localConstraint,
              id: s.id,
              position: s.position,
            };
            return adaptedSetting;
          } )
          .sort( ( a, b ) => {return ( a.position ?? 0 ) - ( b.position ?? 0 );} );
        setCustomSettings( adapted );
        setOriginalSettings( JSON.parse( JSON.stringify( adapted ) ) );
      } catch ( error ) {
        console.error( 'Error fetching settings:', error );
        setCustomSettings( [] );
      }
    };

    fetchSettings();
  }, [ selectedSiteId, token, dispatch ] );

  useEffect( () => {
    const checkHalf = () => {
      try {
        const screenWidth = window.screen?.width || 0;
        const inner = window.innerWidth || 0;
        setIsHalfScreen( inner <= Math.floor( screenWidth * 0.75 ) );
      } catch {
        setIsHalfScreen( false );
      }
    };
    checkHalf();
    window.addEventListener( 'resize', checkHalf );
    return () => { window.removeEventListener( 'resize', checkHalf ); };
  }, [] );

  const moveSetting = ( index: number, direction: 'up' | 'down' ) => {
    const newSettings = [ ...customSettings ];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if ( targetIndex < 0 || targetIndex >= newSettings.length ) {return;}
    [ newSettings[ index ], newSettings[ targetIndex ] ] = [ newSettings[ targetIndex ], newSettings[ index ] ];
    newSettings.forEach( ( setting, idx ) => {
      setting.position = idx;
    } );
    const updatePayload = ( setting: Setting ): FieldSettingPayload => {
      let type: 'string' | 'int' | 'date' | 'list' = 'string';
      if ( setting.type === 'number' ) {type = 'int';}
      else if ( setting.type === 'text' ) {type = 'string';}
      else if ( setting.type === 'date' ) {type = 'date';}
      else if ( setting.type === 'list' ) {type = 'list';}
      const payload: FieldSettingPayload = {
        name: setting.name,
        type,
        siteId: selectedSiteId as UUID,
        position: setting.position,
      };
      if ( type === 'string' && setting.textMaxLength ) {payload.textMaxLength = Number( setting.textMaxLength );}
      if ( type === 'int' ) {
        if ( setting.numberMin !== undefined ) {payload.numberMin = Number( setting.numberMin );}
        if ( setting.numberMax !== undefined ) {payload.numberMax = Number( setting.numberMax );}
      }
      if ( type === 'list' && setting.listItems ) {payload.listItems = setting.listItems;}
      return payload;
    };
    const moved = newSettings[ index ];
    const swapped = newSettings[ targetIndex ];
    setCustomSettings( newSettings );
    if ( moved.id ) {
      FieldSettingService.updateFieldSetting( moved.id, updatePayload( moved ), token, dispatch ).catch( err => {
        console.error( 'Erreur update position:', err );
      } );
    }
    if ( swapped.id ) {
      FieldSettingService.updateFieldSetting( swapped.id, updatePayload( swapped ), token, dispatch ).catch( err => {
        console.error( 'Erreur update position:', err );
      } );
    }
  };

  const isValidSiteId = (): boolean => {
    return selectedSiteId ? GlobalService.isValidGuid( selectedSiteId ) : false;
  };

  const parseMinMax = ( minStr: string | undefined, maxStr: string | undefined ) => {
    const hasMin = minStr !== undefined && minStr !== '';
    const hasMax = maxStr !== undefined && maxStr !== '';
    const min = hasMin ? Number( minStr ) : undefined;
    const max = hasMax ? Number( maxStr ) : undefined;
    if ( hasMin && isNaN( min as number ) ) { return { valid: false, reason: 'minNaN' }; }
    if ( hasMax && isNaN( max as number ) ) { return { valid: false, reason: 'maxNaN' }; }
    if ( hasMin && hasMax && !( ( min as number ) < ( max as number ) ) ) { return { valid: false, reason: 'minNotLessThanMax' }; }
    return { valid: true, min, max };
  };

  const isSettingValidForSave = ( setting: Setting ) => {
    if ( !setting.name || !setting.name.trim() ) { return false; }
    const duplicate = originalSettings
      .filter( item => { return item.id != setting.id; } )
      .some( item => { return item.name.trim().toLowerCase() === setting.name.trim().toLowerCase(); } );
    if ( duplicate ) { return false; }

    if ( setting.type === 'text' ) {
      if ( !setting.constraint || isNaN( Number( setting.constraint ) ) || Number( setting.constraint ) < 1 ) { return false; }
    }

    if ( setting.type === 'number' ) {
      const [ minStr, maxStr ] = setting.constraint?.split( ',' ) || [];
      const parsed = parseMinMax( minStr, maxStr );
      if ( !parsed.valid ) { return false; }
    }

    if ( setting.type === 'list' && setting.constraint.length == 0 ) {
      return false;
    }

    return true;
  };

  const fetchType = (): Promise<GroupedOption[]> => {
    return Promise.resolve( [
      {
        options: [
          { id: 'text', label: t( 'common:text' ) },
          { id: 'number', label: t( 'common:number' ) },
          { id: 'date', label: t( 'common:date' ) },
          { id: 'list', label: t( 'common:list' ) },
        ],
      },
    ] );
  };

  const handleAddSetting = async () => {
    if ( !isValidSiteId() ) {
      console.warn( 'Cannot add setting: invalid or missing siteId' );
      return;
    }

    let constraint = '';
    if ( newSetting.type === 'text' ) {
      const maxLength = Number( newSetting.textMaxLength );
      if ( isNaN( maxLength ) || maxLength < 1 ) {
        return;
      }
      constraint = String( maxLength );
    } else if ( newSetting.type === 'number' ) {
      constraint = [ newSetting.numberMin || '', newSetting.numberMax || '' ].join( ',' );
      const parsed = parseMinMax( newSetting.numberMin, newSetting.numberMax );
      if ( !parsed.valid ) {
        console.warn( 'Invalid number constraint for new setting:', parsed.reason );
        return;
      }
    } else if ( newSetting.type === 'list' ) {
      constraint = newSetting.constraint || '';
    }

    const settingToAdd: Setting = {
      name: newSetting.name,
      type: newSetting.type,
      constraint,
    };

    let newId: { id: UUID } | null = null;
    try {
      const payload: FieldSettingPayload = {
        name: settingToAdd.name,
        type: ( settingToAdd.type === 'text'
          ? 'string'
          : settingToAdd.type === 'number'
            ? 'int'
            : settingToAdd.type ) as 'string' | 'int' | 'date' | 'list',
        siteId: selectedSiteId as UUID,
      };

      if ( settingToAdd.type === 'text' && settingToAdd.constraint ) {
        const maxLength = Number( settingToAdd.constraint ) || 255;
        payload.textMaxLength = maxLength;
      } else if ( settingToAdd.type === 'number' && settingToAdd.constraint ) {
        const [ min, max ] = settingToAdd.constraint.split( ',' ).map( Number );
        payload.numberMin = min || 0;
        payload.numberMax = max || 100;
      } else if ( settingToAdd.type === 'list' && settingToAdd.constraint ) {
        payload.listItems = settingToAdd.constraint
          .split( ',' )
          .map( s => {return s.trim();} )
          .filter( Boolean );
      }

      newId = await FieldSettingService.addFieldSetting( payload, token, dispatch );
    } catch ( error ) {
      console.error( 'Error in addFieldSetting:', error );
      return;
    }

    if ( newId && newId.id ) {
      if ( selectedSiteId ) {
        try {
          const settings = await FieldSettingService.getBySiteId( selectedSiteId!, token, dispatch );

          const adapted = ( Array.isArray( settings ) ? settings : [ settings ] )
            .filter( s => {
              const hasValidId = s.id && GlobalService.isValidGuid( s.id );
              return hasValidId;
            } )
            .map( s => {
              const type = s.type === 'string' ? 'text' : s.type === 'int' ? 'number' : s.type;
              let localConstraint = '';
              if ( type === 'text' ) {
                localConstraint = String( s.textMaxLength || '' );
              } else if ( type === 'number' ) {
                localConstraint = [ s.numberMin, s.numberMax ].filter( v => {return v !== undefined;} ).join( ',' );
              } else if ( type === 'list' ) {
                localConstraint = Array.isArray( s.listItems ) ? s.listItems.join( ',' ) : '';
              }
              return {
                name: s.name || '',
                type,
                constraint: localConstraint,
                id: s.id,
              };
            } );
          setCustomSettings( adapted );
          setOriginalSettings( JSON.parse( JSON.stringify( adapted ) ) );
        } catch ( error ) {
          console.error( 'Error fetching settings:', error );
          setCustomSettings( [] );
        }
      }
      setNewSetting( { name: '', type: 'text', textMaxLength: String( MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE ), numberMin: '', numberMax: '', constraint: '' } );
    }
  };

  const handleSaveSetting = async ( index: number ) => {
    if ( !isValidSiteId() ) {
      console.warn( 'Cannot save setting: invalid or missing siteId' );
      return;
    }

    const setting = customSettings[ index ];

    if ( !setting.id ) {
      console.error( 'No ID found for setting:', setting );
      return;
    }

    if ( setting.type === 'text' ) {
      if ( !setting.constraint || isNaN( Number( setting.constraint ) ) || Number( setting.constraint ) < 1 ) {
        console.warn( 'Invalid text constraint, must be numeric >= 1' );
        return;
      }
    }
    if ( setting.type === 'number' ) {
      const [ minStr, maxStr ] = setting.constraint?.split( ',' ) || [];
      const parsed = parseMinMax( minStr, maxStr );
      if ( !parsed.valid ) {
        console.warn( 'Invalid number constraint for save:', parsed.reason );
        return;
      }
    }

    if ( !setting.name || !setting.name.trim() ) {
      return;
    }

    if ( !GlobalService.isValidGuid( setting.id ) ) {
      console.error( 'Invalid GUID for setting:', setting.id );
      return;
    }

    const payload: FieldSettingPayload = {
      name: setting.name,
      type:
        setting.type === 'text'
          ? 'string'
          : setting.type === 'number'
            ? 'int'
            : ( setting.type as 'date' | 'list' ),
      siteId: selectedSiteId as UUID,
    };

    if ( setting.type === 'text' ) {
      if ( setting.constraint && !isNaN( Number( setting.constraint ) ) ) {
        payload.textMaxLength = Number( setting.constraint );
      }
    } else if ( setting.type === 'number' ) {
      if ( setting.constraint ) {
        const [ minStr, maxStr ] = setting.constraint.split( ',' );
        if ( minStr !== undefined && minStr !== '' && !isNaN( Number( minStr ) ) ) {
          payload.numberMin = Number( minStr );
        }
        if ( maxStr !== undefined && maxStr !== '' && !isNaN( Number( maxStr ) ) ) {
          payload.numberMax = Number( maxStr );
        }
      }
    } else if ( setting.type === 'list' ) {
      if ( setting.constraint ) {
        const items = setting.constraint.split( ',' ).map( s => { return s.trim(); } ).filter( Boolean );
        payload.listItems = items.length > 0 ? items : null;
      } else {
        payload.listItems = [];
      }
    }

    try {
      const isSuccess = await FieldSettingService.updateFieldSetting( setting.id, payload, token, dispatch );
      if ( isSuccess ) {
        const newOriginals = [ ...originalSettings ];
        newOriginals[ index ] = JSON.parse( JSON.stringify( setting ) );
        setOriginalSettings( newOriginals );

        setEditedIndexes( prev => {
          const newEdited = new Set( prev );
          newEdited.delete( index );
          return newEdited;
        } );
      }
    } catch ( error ) {
      console.error( 'Error updating setting:', error );
    }
  };

  const handleChange = ( index: number, key: keyof Setting, value: string ) => {
    const updated = [ ...customSettings ];
    updated[ index ] = { ...updated[ index ], [ key ]: value };

    if ( key === 'type' ) {
      updated[ index ].constraint = '';
    }

    setCustomSettings( updated );

    const original = originalSettings[ index ];
    const current = updated[ index ];

    const hasChanges = original && (
      original.name !== current.name ||
      original.type !== current.type ||
      original.constraint !== current.constraint
    );

    const newEdited = new Set( editedIndexes );
    if ( hasChanges ) {
      newEdited.add( index );
    } else {
      newEdited.delete( index );
    }
    setEditedIndexes( newEdited );
  };

  const handleDeleteSetting = async ( index: number ) => {
    const removed = customSettings[ index ];
    try {
      if ( removed?.id ) {
        const response = await FieldSettingService.deleteFieldSetting( removed.id, token, dispatch );
        if ( response && typeof response === 'object' && 'err' in response ) {
          return;
        } else {
          const updated = [ ...customSettings ];
          updated.splice( index, 1 );
          setCustomSettings( updated );

          const updatedOriginals = [ ...originalSettings ];
          updatedOriginals.splice( index, 1 );
          setOriginalSettings( updatedOriginals );
        }
      }
    } catch ( error ) {
      console.error( 'Error deleting setting:', error );
    }
  };

  return (
    <DataChecker clients={clients} sites={sites}>
      <div className="w-full px-6 pb-8">
        {/* FORMULAIRE AJOUT */}
        <div className="bg-white border border-gray-300 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-5">
            {t( 'translation:add_custom_field' )}
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t( 'common:name' )}
              </label>
              <InputText
                className="w-full"
                value={newSetting.name}
                maxCharacter={MAX_LENGTH_ENTITY.FIELD_SETTING_NAME}
                placeholder={t( 'translation:enter_field_name' )}
                onChange={val => {return setNewSetting( { ...newSetting, name: val } );}}
              />
            </div>

            <div className="lg:col-span-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t( 'common:type' )}
              </label>
              <SelectInput
                className="w-full"
                fetchOptions={fetchType}
                selected={newSetting.type}
                onSelect={val => {
                  const newType = val as 'text' | 'number' | 'date' | 'list';
                  setNewSetting( {
                    ...newSetting,
                    type: newType,
                    textMaxLength: newType === 'text' ? String( MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE ) : '',
                    constraint: ''
                  } );
                }}
              />
            </div>

            <div className="lg:col-span-5">
              {newSetting.type !== 'date' && (
                <>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t( 'common:constraint' )}
                  </label>
                  <div className="flex gap-2">
                    {newSetting.type === 'text' ? (
                      <div className="flex items-center gap-2 w-full">
                        <InputText
                          className={isHalfScreen ? 'w-full truncate overflow-hidden whitespace-nowrap' : 'flex-grow min-w-0 truncate overflow-hidden whitespace-nowrap'}
                          value={newSetting.textMaxLength}
                          min={1}
                          max={MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE}
                          placeholder={t( 'translation:enter_max_length' )}
                          onChange={val => {
                            if ( Number( val ) < 1 ) {
                              setNewSetting( { ...newSetting, textMaxLength: '1' } );
                            } else {
                              setNewSetting( { ...newSetting, textMaxLength: val } );
                            }
                          }}
                          type="number"
                        />
                      </div>
                    ) : newSetting.type === 'number' ? (
                      <>
                        <div className="flex items-center gap-2 flex-1">
                          <InputText
                            className={isHalfScreen ? 'w-full truncate overflow-hidden whitespace-nowrap' : 'flex-grow min-w-0 truncate overflow-hidden whitespace-nowrap'}
                            value={newSetting.numberMin}
                            placeholder={t( 'common:min' )}
                            onChange={val => {return setNewSetting( { ...newSetting, numberMin: val } );}}
                            type="number"
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <InputText
                            className={isHalfScreen ? 'w-full truncate overflow-hidden whitespace-nowrap' : 'flex-grow min-w-0 truncate overflow-hidden whitespace-nowrap'}
                            value={newSetting.numberMax}
                            placeholder={t( 'common:max' )}
                            onChange={val => {return setNewSetting( { ...newSetting, numberMax: val } );}}
                            type="number"
                          />
                        </div>
                      </>
                    ) : newSetting.type === 'list' ? (
                      <ListStringEditor
                        value={newSetting.constraint}
                        onChange={val => {return setNewSetting( prev => { return { ...prev, constraint: val }; } );}}
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={isHalfScreen ? 'mt-5 flex flex-col items-stretch gap-2' : 'mt-5 flex flex-row justify-end items-end gap-2'}>
            <div className={isHalfScreen ? 'w-full' : 'flex-1'}>
              {/* On half-screen, keep full-width space for messages; on full-screen it's flex-1 */}
            </div>
            <div className={isHalfScreen ? 'w-full flex flex-col items-stretch gap-2' : 'flex flex-row items-center gap-2 w-auto'}>
              <button
                onClick={handleAddSetting}
                className={isHalfScreen ? 'bg-green-600 text-white rounded-lg h-9 w-full px-4 flex items-center justify-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm' : 'bg-green-600 text-white rounded-lg h-9 w-auto px-4 flex items-center justify-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm'}
                disabled={
                  !newSetting.name.trim() ||
                  isDuplicateName ||
                  ( newSetting.type === 'text' && ( isNaN( Number( newSetting.textMaxLength ) ) || Number( newSetting.textMaxLength ) < 1 ) ) ||
                  ( newSetting.type === 'number' && !parseMinMax( newSetting.numberMin, newSetting.numberMax ).valid ) ||
                  ( newSetting.type === 'list' && newSetting.constraint.length == 0 )
                }
              >
                <PlusIcon size={4} />
                <span className={isHalfScreen ? 'inline' : 'hidden'}>{t( 'translation:added_fields.add' )} </span>
                <span className={isHalfScreen ? 'hidden' : 'inline'}>{t( 'common:add' )}</span>
              </button>
              {/* Show a warning if duplicate */}
              {isDuplicateName && (
                <span className={isHalfScreen ? 'text-red-600 text-xs font-semibold text-center' : 'text-red-600 text-xs font-semibold text-left'}>{ t( 'translation:duplicate_name_warning' ) }</span>
              )}
            </div>
          </div>
        </div>

        {/* LISTE DES CHAMPS */}
        <div className="bg-white border border-gray-300 rounded-lg overflow-visible">
          {customSettings.length > 0 ? (
            <>
              <div className={isHalfScreen ? 'hidden' : 'bg-gray-50 px-6 py-3 border-b border-gray-300'}>
                <div className="grid grid-cols-12 gap-4 font-medium text-sm text-gray-700">
                  <div className="col-span-4">{t( 'common:name' )}</div>
                  <div className="col-span-3">{t( 'common:type' )}</div>
                  <div className="col-span-4">{t( 'common:constraint' )}</div>
                  <div className="col-span-1 text-center">{t( 'common:action' )}</div>
                </div>
              </div>

              <div className="divide-y divide-gray-200 overflow-visible">
                {customSettings.map( ( setting, idx ) => {return (
                  <div
                    key={setting.id || idx}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className={isHalfScreen ? 'grid grid-cols-1 gap-4 items-center' : 'grid grid-cols-12 gap-4 items-center'}>
                      {isHalfScreen && (
                        <div className="w-full flex justify-end items-center mb-2">
                          <div className="flex items-center gap-2 flex-nowrap">
                            <button
                              className="inline-flex w-9 h-9 border rounded text-xs bg-gray-100 hover:bg-gray-200 items-center justify-center"
                              disabled={idx === 0}
                              title="Monter"
                              onClick={() => { moveSetting( idx, 'up' ); }}
                            >
                              <ArrowUpIcon size={4} />
                            </button>
                            <span className="text-xs font-bold">{ ( setting.position !== undefined ? setting.position + 1 : idx + 1 ) }</span>
                            <button
                              className="inline-flex w-9 h-9 border rounded text-xs bg-gray-100 hover:bg-gray-200 items-center justify-center"
                              disabled={idx === customSettings.length - 1}
                              title="Descendre"
                              onClick={() => { moveSetting( idx, 'down' ); }}
                            >
                              <ArrowDownIcon size={4} />
                            </button>
                          </div>
                        </div>
                      )}
                      <div className={isHalfScreen ? 'hidden' : 'flex flex-col col-span-1 items-center justify-center'}>
                        <button
                          className="mb-1 inline-flex w-9 h-9 border rounded text-xs bg-gray-100 hover:bg-gray-200 items-center justify-center"
                          disabled={idx === 0}
                          title="Monter"
                          onClick={() => { moveSetting( idx, 'up' ); }}
                        >
                          <ArrowUpIcon size={4} />
                        </button>
                        <span className="text-xs font-bold my-1">{ ( setting.position !== undefined ? setting.position + 1 : idx + 1 ) }</span>
                        <button
                          className="inline-flex w-9 h-9 border rounded text-xs bg-gray-100 hover:bg-gray-200 items-center justify-center"
                          disabled={idx === customSettings.length - 1}
                          title="Descendre"
                          onClick={() => { moveSetting( idx, 'down' ); }}
                        >
                          <ArrowDownIcon size={4} />
                        </button>
                      </div>
                      <div className={isHalfScreen ? 'col-span-1' : 'col-span-3'}>
                        <label className={isHalfScreen ? 'block text-sm font-semibold text-gray-700 mb-2' : 'hidden'}>{t( 'common:name' )}</label>
                        <InputText
                          className="w-full"
                          value={setting.name}
                          onChange={val => {return handleChange( idx, 'name', val );}}
                        />
                      </div>

                      <div className={isHalfScreen ? 'col-span-1' : 'col-span-3'}>
                        <label className={isHalfScreen ? 'block text-sm font-semibold text-gray-700 mb-2' : 'hidden'}>{t( 'common:type' )}</label>
                        <SelectInput
                          className="w-full"
                          fetchOptions={fetchType}
                          selected={setting.type}
                          onSelect={val => {return handleChange( idx, 'type', val );}}
                        />
                      </div>

                      <div className={isHalfScreen ? 'col-span-1' : 'col-span-4'}>
                        <label className={isHalfScreen ? 'block text-sm font-semibold text-gray-700 mb-2' : 'hidden'}>{t( 'common:constraint' )}</label>
                        {setting.type === 'text' ? (
                          <div className="flex items-center gap-2">
                            <InputText
                              className="flex-grow min-w-0 truncate overflow-hidden whitespace-nowrap"
                              value={setting.constraint}
                              min={1}
                              max={MAX_LENGTH_ENTITY.FIELD_SETTING_VALUE}
                              placeholder={t( 'translation:enter_max_length' )}
                              onChange={val => {return handleChange( idx, 'constraint', val );}}
                              type="number"
                            />
                          </div>
                        ) : setting.type === 'number' ? (
                          <div className="flex gap-2">
                            <div className="flex items-center gap-2 flex-1">
                              <InputText
                                className={isHalfScreen ? 'w-full truncate overflow-hidden whitespace-nowrap' : 'w-full min-w-0 truncate overflow-hidden whitespace-nowrap'}
                                value={setting.constraint?.split( ',' )[ 0 ] || ''}
                                placeholder={t( 'common:min' )}
                                onChange={val => {
                                  const [ , max ] = setting.constraint?.split( ',' ) || [];
                                  handleChange( idx, 'constraint', `${val},${max || ''}` );
                                }}
                                type="number"
                              />
                            </div>
                            <div className="flex items-center gap-2 flex-1">
                              <InputText
                                className={isHalfScreen ? 'w-full truncate overflow-hidden whitespace-nowrap' : 'w-full min-w-0 truncate overflow-hidden whitespace-nowrap'}
                                value={setting.constraint?.split( ',' )[ 1 ] || ''}
                                placeholder={t( 'common:max' )}
                                onChange={val => {
                                  const [ min ] = setting.constraint?.split( ',' ) || [];
                                  handleChange( idx, 'constraint', `${min || ''},${val}` );
                                }}
                                type="number"
                              />
                            </div>
                          </div>
                        ) : setting.type === 'list' ? (
                          <ListStringEditor
                            value={setting.constraint}
                            onChange={val => {return handleChange( idx, 'constraint', val );}}
                          />
                        ) : (
                          <></>
                        )}
                      </div>

                      <div className="col-span-1 flex items-center justify-end gap-2 min-h-[48px]">
                        {
                          editedIndexes.has( idx ) ?
                            <button
                              onClick={() => {return handleSaveSetting( idx );}}
                              className="bg-blue-600 text-white rounded-lg h-9 w-9 flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t( 'common:save' )}
                              disabled={!isSettingValidForSave( setting )}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            :
                            <button
                              onClick={() => {return handleDeleteSetting( idx );}}
                              className="bg-red-600 text-white rounded-lg h-9 w-9 flex items-center justify-center hover:bg-red-700 transition-colors"
                              title={t( 'common:delete' )}
                            >
                              <TrashIcon size={4} />
                            </button>
                        }
                      </div>
                    </div>
                  </div>
                );} )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-3">
                <PlusIcon size={6} className="text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-700 mb-1">
                {t( 'common:no_data' )}
              </p>
              <p className="text-sm text-gray-500">
                {t( 'translation:add_first_custom_field' )}
              </p>
            </div>
          )}
        </div>
      </div>
    </DataChecker>
  );

  function ListStringEditor( {
    value,
    onChange,
  }: {
    value: string;
    onChange: ( val: string ) => void;
  } ) {
    const [ items, setItems ] = React.useState<string[]>( () =>
    {return value ? value.split( ',' ).map( s => {return s.trim();} ).filter( Boolean ) : [];}
    );
    const [ input, setInput ] = React.useState( '' );
    const inputRef = React.useRef<HTMLInputElement | null>( null );

    React.useEffect( () => {
      setItems( value ? value.split( ',' ).map( s => {return s.trim();} ).filter( Boolean ) : [] );
    }, [ value ] );

    const addItem = () => {
      const trimmed = input.trim();
      if ( trimmed && !items.includes( trimmed ) ) {
        const newItems = [ ...items, trimmed ];
        setItems( newItems );
        onChange( newItems.join( ',' ) );
        setInput( '' );
        // keep focus on input after adding
        setTimeout( () => { inputRef.current?.focus(); }, 0 );
      }
    };

    const removeItem = ( idx: number ) => {
      if ( items.length > 0 ) {
        const newItems = items.filter( ( _, i ) => {return i !== idx;} );
        setItems( newItems );
        onChange( newItems.join( ',' ) );
        // keep focus on input after removing
        setTimeout( () => { inputRef.current?.focus(); }, 0 );
      }
    };

    return (
      <div className="w-full">
        <div className="w-full flex flex-wrap gap-2 mb-2">
          {items.map( ( item, idx ) => {return (
            <span
              key={idx}
              className="relative w-full sm:inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-2 rounded-lg text-sm font-medium border border-blue-200 sm:max-w-[200px] pr-12 sm:pr-3 min-h-[40px]"
            >
              <span className={isHalfScreen ? 'truncate block' : 'truncate block max-w-[140px]'}>{item}</span>
              {items.length >= 0 && (
                <button
                  type="button"
                  className={isHalfScreen ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center transition-colors absolute right-2 bottom-2 z-10' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center transition-colors static ml-1 z-10'}
                  onClick={() => {return removeItem( idx );}}
                  title={t( 'common:delete' )}
                >
                </button>
              )}
            </span>
          );} )}
        </div>

        <div className={isHalfScreen ? 'flex flex-col items-stretch gap-2' : 'flex flex-row items-center gap-2'}>
          <input
            ref={inputRef}
            className="flex-grow min-w-[160px] truncate overflow-hidden whitespace-nowrap border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            value={input}
            onChange={e => {return setInput( e.target.value );}}
            onKeyDown={e => {
              if ( e.key === 'Enter' ) {
                e.preventDefault();
                addItem();
              }
            }}
            placeholder={t( 'translation:list_item' )}
          />
          <button
            type="button"
            className={isHalfScreen ? 'bg-green-600 text-white rounded-lg px-3 py-1.5 hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full h-9 flex items-center justify-center' : 'bg-green-600 text-white rounded-lg px-3 py-1.5 hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed w-9 h-9 flex items-center justify-center'}
            onClick={addItem}
            disabled={!input.trim() || items.includes( input.trim() )}
            title={t( 'common:add' )}
          >
            +
          </button>
        </div>
      </div>
    );
  }
};

export default SettingsSite;